import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "featured" | "compact";
}

export default function ArticleCard({ article, variant = "default" }: ArticleCardProps) {
  if (variant === "featured") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:shadow-md transition-all duration-300 cursor-pointer no-underline"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          <div className="relative overflow-hidden h-48 md:h-full">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="p-5 md:p-6 flex flex-col justify-center">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="px-2.5 py-0.5 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
                {article.category === "plot-analysis" ? "剧情解析" :
                 article.category === "reviews" ? "观后感" :
                 article.category === "education" ? "教育意义" :
                 article.category === "character-analysis" ? "角色分析" :
                 article.category === "spelling-bee" ? "拼写比赛" : "励志成长"}
              </span>
              <span className="text-xs text-foreground-500">{article.readTime}</span>
            </div>
            <h3 className="text-lg md:text-xl font-bold text-foreground-950 group-hover:text-primary-500 transition-colors mb-2 line-clamp-2" style={{ fontFamily: "var(--font-heading)" }}>
              {article.title}
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-3 mt-4">
              <div className="w-7 h-7 rounded-full overflow-hidden bg-background-200">
                <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
              </div>
              <span className="text-xs text-foreground-500">{article.author} · {article.publishDate}</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex items-start gap-4 p-4 bg-background-50 border border-background-200/70 rounded-lg hover:shadow-md transition-all duration-300 cursor-pointer no-underline"
      >
        <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0 bg-background-200">
          <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2">
            {article.title}
          </h4>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className="text-xs text-foreground-500">{article.author}</span>
            <span className="text-xs text-foreground-400">·</span>
            <span className="text-xs text-foreground-500">{article.publishDate}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:shadow-md transition-all duration-300 cursor-pointer no-underline"
    >
      <div className="relative overflow-hidden h-44">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3">
          <span className="px-2.5 py-0.5 bg-background-50/90 text-foreground-950 rounded-full text-xs font-medium backdrop-blur-sm">
            {article.category === "plot-analysis" ? "剧情解析" :
             article.category === "reviews" ? "观后感" :
             article.category === "education" ? "教育意义" :
             article.category === "character-analysis" ? "角色分析" :
             article.category === "spelling-bee" ? "拼写比赛" : "励志成长"}
          </span>
        </div>
      </div>
      <div className="p-4 md:p-5">
        <h3 className="text-base font-bold text-foreground-950 group-hover:text-primary-500 transition-colors mb-2 line-clamp-2" style={{ fontFamily: "var(--font-heading)" }}>
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full overflow-hidden bg-background-200">
              <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
            </div>
            <span className="text-xs text-foreground-500">{article.author}</span>
          </div>
          <span className="text-xs text-foreground-400">{article.readTime}</span>
        </div>
      </div>
    </Link>
  );
}